import {
  Box,
  ListItemIcon,
  Stack,
  styled,
  Theme,
  Typography,
} from '@mui/material';
import { isEmpty } from 'lodash';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useWatch } from 'react-hook-form';

import { Button } from '@/components/form/baseInputs/Button';
import { DeleteButton } from '@/components/form/baseInputs/Button/DeleteButton';
import { TypeaheadSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { TypeaheadSelectInput } from '@/components/form/baseInputs/TypeaheadSelectInput/TypeaheadSelectInput';
import { UserPlus02Icon } from '@/components/icons/UserPlus02Icon';
import { useReportError } from '@/hooks/useReportError';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { MatcherSuggestion } from '@/modules/aiSuggestions/aiSuggestions.contracts';
import { AISuggestionsMatcherVariant } from '@/modules/aiSuggestions/AISuggestionsMatcher/AISuggestionsMatcher.types';
import {
  getMatcherRowSelectionPlaceholder,
  getMatchFromNewId,
  getSuggestedActorDisplayName,
  getSuggestionMatchIDFromDpRecipientKGV2Nodes,
  getSuggestionMatchIDFromExistingIDField,
  getSuggestionMatchIDFromKGV2Statements,
  getSuggestionMatchIDFromReferenceOnboardingSuggestionField,
  getSuggestionMatchIDFromSurvivingSpouse,
  isDPMatcherVariant,
} from '@/modules/aiSuggestions/AISuggestionsMatcher/AISuggestionsMatcher.utils';
import { useAiSuggestionsMatcherContext } from '@/modules/aiSuggestions/AISuggestionsMatcher/context/aiSuggestionsMatcher.context';
import { useAISuggestionsMatcherGroupedOptions } from '@/modules/aiSuggestions/AISuggestionsMatcher/hooks/useAISuggestionsMatcherGroupedOptions';
import { useSuggestedRecipientOptions } from '@/modules/aiSuggestions/AISuggestionsMatcher/hooks/useSuggestedRecipientOptions';
import {
  FormAwareLocateSuggestionButton,
  FormAwareLocateSuggestionButtonVariant,
} from '@/modules/aiSuggestions/components/forms/FormAwareLocateSuggestionButton';
import { SuggestionNotesHelp } from '@/modules/aiSuggestions/components/SuggestionNotesHelp';
import { BASIC_INFORMATION_SUBFORM_NAMESPACE } from '@/modules/entities/BasicInformationSubform/BasicInformationSubform.types';
import { SURVIVING_SPOUSE_SENTINEL } from '@/modules/entities/EntityBeneficiariesSubform/EntityBeneficiariesSubform.constants';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { containerQuery } from '@/styles/containerQueries';
import { COLORS } from '@/styles/tokens/colors';
import { diagnostics } from '@/utils/diagnostics';
import { getNodes } from '@/utils/graphqlUtils';
import { getPulidKind } from '@/utils/pulid';

enum AISuggestionMatchAction {
  ADD = 'ADD',
}

// Breakpoint for stacking to two rows (narrower than the default 840px)
const NARROW_BREAKPOINT = 600;

// Container with container-type for container queries
const ResponsiveRowContainer = styled(Box)(({ theme }: { theme: Theme }) => ({
  containerType: 'inline-size',
  width: '100%',
  border: `1px solid ${theme.palette.grey[200]}`,
  borderRadius: '4px',
  backgroundColor: theme.palette.common.white,
}));

// Main content wrapper - switches from row to column at narrow widths
const ResponsiveRowContent = styled(Stack)(({ theme }: { theme: Theme }) => ({
  width: '100%',
  padding: theme.spacing(1.5, 2),
  gap: theme.spacing(2),
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  [containerQuery(theme).down(NARROW_BREAKPOINT)]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(2),
  },
}));

// Info section (search icon + recipient info)
const InfoSection = styled(Stack)(({ theme }: { theme: Theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(2),
  flex: 1,
  [containerQuery(theme).down(NARROW_BREAKPOINT)]: {
    gap: theme.spacing(1.5),
  },
}));

// Actions section (select + buttons) - changes alignment at narrow widths
const ActionsSection = styled(Stack)(({ theme }: { theme: Theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  flex: 1,
  justifyContent: 'flex-end',
  [containerQuery(theme).down(NARROW_BREAKPOINT)]: {
    justifyContent: 'flex-start',
    flex: 'none',
    width: '100%',
  },
}));

// Wrapper for SelectInput that grows to fill available space in narrow mode
const SelectInputWrapper = styled(Box)(({ theme }: { theme: Theme }) => ({
  width: 200,
  minWidth: 100,
  [containerQuery(theme).down(NARROW_BREAKPOINT)]: {
    flex: 1,
    width: 'auto',
  },
}));

interface AISuggestedPersonRowProps {
  variant: AISuggestionsMatcherVariant;
  suggestion: MatcherSuggestion;
  onSetSuggestionMatch: (
    suggestionId: string,
    matched: {
      clientProfileId?: string;
      clientOrganizationId?: string;
      entityId?: string;
      testamentaryId?: string;
      survivingSpouse?: boolean;
    }
  ) => void;
  onOpenAddAnythingModal: () => void;
}

export function AISuggestionMatcherRow({
  variant,
  suggestion,
  onSetSuggestionMatch,
  onOpenAddAnythingModal,
}: AISuggestedPersonRowProps) {
  const { reportError } = useReportError();
  const trackUserEvent = useTrackUserEvent();
  const { householdId } = useHouseholdDetailsContext();

  const {
    matchedSuggestions,
    confirmRow,
    ignoreRow,
    newlyAddedIDs,
    matcherConfigs: {
      subformArrayFieldUpdateProps,
      subformArrayFieldOnConflict,
    },
  } = useAiSuggestionsMatcherContext();

  const [selectedOption, setSelectedOption] = useState('');

  useEffect(() => {
    const match = matchedSuggestions[suggestion.id];

    if (!match) {
      setSelectedOption('');
      return;
    }

    if (match.survivingSpouse) {
      setSelectedOption(SURVIVING_SPOUSE_SENTINEL);
      return;
    }

    const matchedId =
      match.clientProfileId ||
      match.clientOrganizationId ||
      match.entityId ||
      match.testamentaryId;
    if (matchedId) {
      setSelectedOption(matchedId);
    }
  }, [matchedSuggestions, suggestion.id]);

  const singleGrantorFieldValue = useWatch({
    name: `${BASIC_INFORMATION_SUBFORM_NAMESPACE}.grantor.clientProfileId`,
  });

  const {
    options: groupedOptions,
    household,
    pendingSuggestions,
  } = useAISuggestionsMatcherGroupedOptions({
    matcherVariant: variant,
    suggestionKind: suggestion.kind,
    suggestionId: suggestion.id,
  });

  // Check if the selected option exists in the available options.
  // If not, show empty so the placeholder "Select recipient" displays.
  const isSelectionValid = useMemo(() => {
    if (!selectedOption) return false;
    return groupedOptions.some((opt) => opt.value === selectedOption);
  }, [selectedOption, groupedOptions]);

  const displaySelectedOption = isSelectionValid ? selectedOption : '';

  const handleAddNew = useCallback(() => {
    onOpenAddAnythingModal();
    trackUserEvent('ai_suggestion people-matcher add-new', {
      suggestionKind: suggestion.kind,
    });
  }, [onOpenAddAnythingModal, suggestion.kind, trackUserEvent]);

  const actionOptions: TypeaheadSelectInputOption<string>[] = useMemo(() => {
    return [
      {
        value: AISuggestionMatchAction.ADD,
        display: 'Add new',
        startAdornment: (
          <ListItemIcon>
            <UserPlus02Icon size={16} />
          </ListItemIcon>
        ),
        // Don't allow adding a new item if the single grantor field is already set.
        disabled:
          variant === AISuggestionsMatcherVariant.SINGLE_GRANTOR &&
          !!singleGrantorFieldValue,
        onSelectAction: handleAddNew,
      },
    ];
  }, [handleAddNew, singleGrantorFieldValue, variant]);

  const updateSuggestionMatch = useCallback(
    (selectedId: string) => {
      const match = getMatchFromNewId(selectedId);

      if (!match) {
        reportError(
          `[AISuggestionMatcherRow][updateSuggestionMatch] WARNING: unsupported selected suggestion match option: ${selectedId}`,
          null,
          { selectedId }
        );
        return;
      }

      onSetSuggestionMatch(suggestion.id, match);

      trackUserEvent('ai_suggestion people-matcher match-existing', {
        suggestionKind: suggestion.kind,
      });
    },
    [
      onSetSuggestionMatch,
      reportError,
      suggestion.id,
      suggestion.kind,
      trackUserEvent,
    ]
  );

  const handleSelectActionOption = useCallback(
    (selectedOption: string) => {
      setSelectedOption(selectedOption);
      updateSuggestionMatch(selectedOption);
    },
    [updateSuggestionMatch]
  );

  const {
    options: suggestedRecipientOptions,
    creating,
    selectionRevision,
  } = useSuggestedRecipientOptions({
    householdId: householdId ?? '',
    household,
    pendingSuggestions,
    enabled: isDPMatcherVariant(variant),
    recipientNodeIds: getNodes(suggestion.dpRecipientKgV2Nodes).map(
      (node) => node.id
    ),
    referenceOnboardingSuggestionId:
      suggestion.referenceAiOnboardingSuggestion?.id,
    onCreated: handleSelectActionOption,
  });

  // Disable the selector if single-variant, and we've already selected
  // another suggestion as a match.
  const disabled = useMemo(() => {
    if (
      variant !== AISuggestionsMatcherVariant.SINGLE_GRANTOR ||
      isEmpty(matchedSuggestions) ||
      selectedOption
    ) {
      return false;
    }

    return Object.values(matchedSuggestions).some(
      (m) => m.clientProfileId || m.clientOrganizationId
    );
  }, [matchedSuggestions, selectedOption, variant]);

  useLayoutEffect(() => {
    if (selectedOption) return;

    // Surviving-spouse pre-match runs ahead of KGV2 because KGV2 would
    // otherwise resolve the suggestion to a specific spouse client profile,
    // overriding the backend's intent to match against "the spouse who
    // survives" rather than a specific person.
    let preMatchedID: string | undefined | null =
      getSuggestionMatchIDFromSurvivingSpouse(suggestion);

    if (!preMatchedID) {
      preMatchedID =
        getSuggestionMatchIDFromDpRecipientKGV2Nodes(suggestion) ||
        getSuggestionMatchIDFromKGV2Statements(suggestion);
    }

    preMatchedID =
      preMatchedID ||
      getSuggestionMatchIDFromExistingIDField(suggestion) ||
      getSuggestionMatchIDFromReferenceOnboardingSuggestionField(suggestion);

    if (!preMatchedID) {
      return; // no-op.
    }
    if (
      preMatchedID !== SURVIVING_SPOUSE_SENTINEL &&
      !getPulidKind(preMatchedID)
    ) {
      diagnostics.warn(
        `[AISuggestionMatcherRow] warning! invalid preMatchedID=${preMatchedID} for suggestionID=${suggestion.id} suggestionKind=${suggestion.kind}. skipping matching...`
      );
      return;
    }
    setSelectedOption(preMatchedID);
    updateSuggestionMatch(preMatchedID);
  }, [selectedOption, suggestion, updateSuggestionMatch]);

  const handleAcceptSuggestion = useCallback(() => {
    trackUserEvent('ai_suggestion accept', {
      suggestionKind: suggestion.kind,
      count: 1,
    });
    confirmRow(suggestion.id);
  }, [confirmRow, suggestion.id, suggestion.kind, trackUserEvent]);

  // "Update" when matched to an existing entity AND this suggestion's field uses
  // overwrite mode (e.g. beneficial owners). Append-mode fields like key people
  // always say "Create" since they add a new row rather than overwriting one.
  const fieldOnConflict =
    subformArrayFieldUpdateProps.find((f) =>
      f.suggestionKinds?.includes(suggestion.kind)
    )?.onConflict ?? subformArrayFieldOnConflict;
  const acceptButtonLabel =
    isSelectionValid &&
    !newlyAddedIDs.includes(selectedOption) &&
    fieldOnConflict === 'overwrite'
      ? 'Update'
      : 'Create';

  const handleRejectSuggestion = useCallback(() => {
    trackUserEvent('ai_suggestion reject', {
      suggestionKind: suggestion.kind,
      count: 1,
    });
    ignoreRow(suggestion.id);
  }, [ignoreRow, suggestion.id, suggestion.kind, trackUserEvent]);

  if (!householdId || !suggestion) {
    return null;
  }

  return (
    <ResponsiveRowContainer data-testid="ai-suggestions-matcher-row">
      <ResponsiveRowContent>
        <InfoSection>
          <FormAwareLocateSuggestionButton
            variant={FormAwareLocateSuggestionButtonVariant.MATCHER}
            suggestion={suggestion}
          />
          <Stack direction="column" gap={0.25} flex={1}>
            <Typography variant="body-xs" component="div">
              Identified {getMatcherRowSelectionPlaceholder(suggestion.kind)}
            </Typography>
            <Typography
              variant="title-xs"
              component="div"
              color={COLORS.TEXT.PRIMARY}
            >
              {getSuggestedActorDisplayName(suggestion)}
              {/* Rendered inline (not as a flex sibling) so the notes icon
                  trails the last word and tracks the text when the name wraps. */}
              <SuggestionNotesHelp notes={suggestion.dpRecipient?.notes} />
            </Typography>
          </Stack>
        </InfoSection>
        <ActionsSection>
          <SelectInputWrapper>
            <TypeaheadSelectInput<string>
              key={selectionRevision}
              sx={{ width: '100%' }}
              size="xs"
              required
              value={displaySelectedOption}
              hideLabel
              label="Select recipient"
              dropdownWidth={isDPMatcherVariant(variant) ? 360 : undefined}
              options={[
                ...actionOptions,
                ...suggestedRecipientOptions,
                ...groupedOptions,
              ]}
              onChange={(_event, value) => {
                if (value) handleSelectActionOption(value);
              }}
              disabled={disabled || creating}
              placeholder="Select recipient"
              groupBy={(option) => option.groupName ?? ''}
            />
          </SelectInputWrapper>
          <Button
            variant="primary"
            size="xs"
            onClick={handleAcceptSuggestion}
            disabled={!isSelectionValid || creating}
            data-testid={`acceptSuggestion_${suggestion.kind}`}
          >
            {acceptButtonLabel}
          </Button>
          <DeleteButton
            size="xs"
            variant="transparent"
            onConfirmDelete={handleRejectSuggestion}
            data-testid={`rejectSuggestion_${suggestion.kind}`}
            square
          />
        </ActionsSection>
      </ResponsiveRowContent>
    </ResponsiveRowContainer>
  );
}
